import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useDialogProps from 'Hooks/useFloatingProps';
import { Slot } from 'nano-slots';
import {
    useFloating,
    useInteractions,
    useRole,
    useDismiss,
    FloatingPortal,
    FloatingOverlay,
    FloatingFocusManager,
} from '@floating-ui/react';
import { cn } from 'Utils/cn';
import { motion, AnimatePresence } from 'framer-motion';
import useMediaQuery from 'Hooks/useMediaQuery';

const sizeClasses = {
    xs: 'w-64',
    sm: 'w-80',
    md: 'w-[496px]',
    lg: 'w-[585px]',
    xl: 'w-[872px]',
};

const DrawerWithPreview = forwardRef(function DrawerWithPreview(props, ref) {
    const { open, onClose, children, className, size = 'md', currentId } = useDialogProps(props);

    const isDesktop = useMediaQuery('(min-width: 768px)');
    const [hasPreview, setHasPreview] = useState(false);

    const scrollRef = useRef(null);
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        setScrollY(0);
        scrollRef.current?.scrollTo?.(0, 0);
    }, [currentId]);

    useImperativeHandle(ref, () => {
        return {
            resetScroll: () => {
                setScrollY(0);
                scrollRef.current?.scrollTo?.(0, 0);
            },
        };
    }, []);

    const { floating, context } = useFloating({
        open,
        onOpenChange: (open) => {
            if (!open) {
                onClose();
                setScrollY(0);
            }
        },
    });

    const { getFloatingProps } = useInteractions([
        useRole(context, { role: 'dialog' }),
        useDismiss(context, {
            outsidePress: (event) => {
                // Don't dismiss the drawer when clicking inside a dialog overlay
                return !event.target.closest('.z-modal-backdrop');
            },
        }),
    ]);

    const handleClickOutside = () => {
        if (!hasPreview || !isDesktop) {
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <FloatingPortal>
                    <FloatingOverlay lockScroll className="z-drawer-with-preview inset-0 fixed overflow-x-hidden!">
                        <FloatingFocusManager context={context}>
                            <div
                                {...getFloatingProps({
                                    ref: floating,
                                    className: 'h-full flex',
                                })}
                            >
                                <div onClick={handleClickOutside} className="grow h-full">
                                    <motion.div
                                        className="h-full hidden md:block"
                                        initial={{
                                            opacity: 0,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            transition: { delay: 0.4 },
                                        }}
                                        exit={{
                                            opacity: 0,
                                        }}
                                        transition={{ ease: 'easeInOut', duration: 0.2 }}
                                    >
                                        <Slot name="drawer-stack-preview-desktop" onChange={setHasPreview} />
                                    </motion.div>
                                </div>

                                <motion.div
                                    className={cn(
                                        'h-full shrink-0 bg-white shadow-lg max-w-full overflow-y-auto overflow-x-hidden transition-[width] duration-200 ease-in-out',
                                        sizeClasses[size],
                                        className,
                                    )}
                                    ref={scrollRef}
                                    style={{
                                        '--scroll-y': scrollY,
                                    }}
                                    tabIndex={-1}
                                    aria-modal="true"
                                    initial={{
                                        x: '100%',
                                        opacity: 1,
                                    }}
                                    onScroll={(ev) => {
                                        setScrollY(ev.currentTarget.scrollTop);
                                    }}
                                    animate={{
                                        x: 0,
                                        opacity: 1,
                                    }}
                                    exit={{
                                        x: '100%',
                                        opacity: 0.7,
                                    }}
                                    transition={{ ease: 'easeInOut', duration: 0.2 }}
                                >
                                    {children}
                                    <Slot name="drawer-stack-footer" />
                                </motion.div>
                            </div>
                        </FloatingFocusManager>
                    </FloatingOverlay>
                </FloatingPortal>
            )}
        </AnimatePresence>
    );
});

export default DrawerWithPreview;

DrawerWithPreview.propTypes = {
    open: PropTypes.bool.isRequired,
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func.isRequired,
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    classes: PropTypes.object,
    preview: PropTypes.node,
};
